<script>

import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import ModalWrapper from '@/components/modals/Modal-wrapper.vue';
import {
  CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusCircleOutlined,
} from '@ant-design/icons-vue';

const b = bem('frames-sizes');

export default {
  name: 'FramesSizes',
  components: {
    CheckOutlined,
    CloseOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusCircleOutlined,
    ModalWrapper,
  },
  data() {
    return {
      selectedRatio: null,
      editableData: {},
      sizeWidth: null,
      sizeHeight: null,
      sizeRatio: null,
      isShowPopup: false,
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getFrameSizesAction: 'createTemplate/getFrameSizes',
      editFrameSizeAction: 'createTemplate/editFrameSize',
      createFrameSizeAction: 'createTemplate/createFrameSize',
      deleteFrameSizeAction: 'createTemplate/deleteFrameSize',
    }),
    openAddWindow() {
      this.isShowPopup = true;
    },
    selectRatio(ratio) {
      this.selectedRatio = ratio;
    },
    startEdit(record) {
      this.editableData = { ...record };
    },
    cancelEdit() {
      this.editableData = {};
    },
    saveCard() {
      this.editFrameSizeAction({
        id: this.editableData.id,
        body: {
          name: this.editableData.name,
          width: this.editableData.width,
          height: this.editableData.height,
          ratio: this.editableData.ratio,
        },
      });
      this.editableData = {};
    },
    save() {
      this.createFrameSizeAction({
        body: {
          width: this.sizeWidth,
          height: this.sizeHeight,
          ratio: this.sizeRatio,
        },
      });
      this.cancel();
    },
    cancel() {
      this.sizeWidth = null;
      this.sizeHeight = null;
      this.sizeRatio = null;
      this.editableData = {};
      this.isShowPopup = false;
    },
  },
  computed: {
    ...mapState({
      sizes: (state) => state.createTemplate.sizes,
    }),
    availableRatios() {
      const ratios = [...new Set(this.sizes.map((s) => s.ratio).filter(Boolean))];
      return ratios.sort((a, c) => {
        const aFirst = parseInt(a.split(':')[0], 10) || 0;
        const cFirst = parseInt(c.split(':')[0], 10) || 0;
        return aFirst - cFirst;
      });
    },
    hasNoRatioSizes() {
      return this.sizes.some((s) => !s.ratio);
    },
    activeRatio() {
      if (this.selectedRatio === 'no-ratio') return 'no-ratio';
      if (this.selectedRatio) return this.selectedRatio;
      if (this.availableRatios.length > 0) return this.availableRatios[0];
      return 'no-ratio';
    },
    filteredSizes() {
      const filtered = this.activeRatio === 'no-ratio'
        ? this.sizes.filter((s) => !s.ratio)
        : this.sizes.filter((s) => s.ratio === this.activeRatio);
      return [...filtered].sort((a, c) => a.width - c.width);
    },
  },
  mounted() {
    this.getFrameSizesAction();
  },
};
</script>

<template>
  <div :class="b()">
    <div :class="b('create-button')">
      <a-button
        @click="openAddWindow()"
        :class="b('plus-btn')"
      >
        Add size <PlusCircleOutlined :class="b('plus')" />
      </a-button>
    </div>

    <modal-wrapper v-model:show="isShowPopup" width="400px">
      <div :class="b('modal-content')">
        <p>Enter height and width for new size frame.</p>
        <h4>Width:</h4>
        <a-input
          placeholder="Width"
          style="max-width: 300px"
          v-model:value="sizeWidth"
        />
        <h4>Height:</h4>
        <a-input
          placeholder="Height"
          style="max-width: 300px"
          v-model:value="sizeHeight"
        />
        <h4>Ratio:</h4>
        <a-input
          placeholder="e.g. 3:2"
          style="max-width: 300px"
          v-model:value="sizeRatio"
        />
        <div :class="b('modal-buttons-wrap')">
          <a-button
            type="primary"
            html-type="button"
            @click="save()"
            :disabled="!sizeWidth || !sizeHeight"
          >
            Save
          </a-button>
          <a-button
            html-type="button"
            @click="cancel()"
          >
            Cancel
          </a-button>
        </div>
      </div>
    </modal-wrapper>

    <div :class="b('filters-section')">
      <h3 :class="b('filters-title')">Aspect Ratio</h3>
      <div :class="b('filters')">
        <a-button
          v-for="ratio in availableRatios"
          :key="ratio"
          :class="b('filter-btn', { active: activeRatio === ratio })"
          @click="selectRatio(ratio)"
        >
          {{ ratio }}
        </a-button>
        <a-button
          v-if="hasNoRatioSizes"
          :class="b('filter-btn', { active: activeRatio === 'no-ratio' })"
          @click="selectRatio('no-ratio')"
        >
          No ratio
        </a-button>
      </div>
    </div>

    <div :class="b('cards')">
      <div
        v-for="size in filteredSizes"
        :key="size.id"
        :class="b('card', { editing: editableData.id === size.id })"
      >
        <template v-if="editableData.id === size.id">
          <div :class="b('card-field')">
            <span :class="b('card-label')">Name:</span>
            <a-input
              v-model:value="editableData.name"
              placeholder="Name"
              size="small"
            />
          </div>
          <div :class="b('card-field')">
            <span :class="b('card-label')">Width:</span>
            <a-input
              v-model:value="editableData.width"
              type="number"
              step="0.01"
              size="small"
            />
          </div>
          <div :class="b('card-field')">
            <span :class="b('card-label')">Height:</span>
            <a-input
              v-model:value="editableData.height"
              type="number"
              step="0.01"
              size="small"
            />
          </div>
          <div :class="b('card-field')">
            <span :class="b('card-label')">Ratio:</span>
            <a-input
              v-model:value="editableData.ratio"
              placeholder="e.g. 3:2"
              size="small"
            />
          </div>
          <div :class="b('card-actions')">
            <a-button type="primary" size="small" @click="saveCard">
              <CheckOutlined /> Save
            </a-button>
            <a-button size="small" @click="cancelEdit">
              <CloseOutlined /> Cancel
            </a-button>
          </div>
        </template>

        <template v-else>
          <div :class="b('card-name')">{{ size.name || `${size.width}x${size.height}` }}</div>
          <div :class="b('card-dimensions')">{{ size.width }} × {{ size.height }}</div>
          <div :class="b('card-ratio')" v-if="size.ratio">{{ size.ratio }}</div>
          <div :class="b('card-actions')">
            <a-button size="small" @click="startEdit(size)">
              <EditOutlined />
            </a-button>
            <a-popconfirm title="Sure to delete?" @confirm="deleteFrameSizeAction(size.id)">
              <a-button size="small" danger>
                <DeleteOutlined />
              </a-button>
            </a-popconfirm>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style src="./style.scss" lang="scss" scoped />
